import React, { useState, useRef, useEffect, useMemo, useCallback, useImperativeHandle, forwardRef } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, ActivityIndicator, Image, Platform, InteractionManager, ViewStyle, TextStyle } from 'react-native';
import { ChevronLeft, Heart, FingerprintPattern as Fingerprint } from 'lucide-react-native';
import { useAuth } from '@/hooks/useAuth';
import { useTranslation } from '@/hooks/useTranslation';
import { useTheme, darkTheme, Theme } from '@/hooks/useTheme';
import { hexToRgba } from '@/utils/colorUtils';
import { validateEmail, validatePhoneNumber, isPhoneNumber, cleanPhoneNumber } from '@/utils/contactValidators';
import { supabaseUrl } from '@/lib/supabase';
import { appStabilityGuard } from '@/utils/appStabilityGuard';
import { persistentCountryCache } from '@/services/persistentCountryCacheService';

const COUNTRY_DIAL_CODES: Record<string, string> = {
  fr: '+33', be: '+32', ch: '+41', de: '+49', es: '+34',
  it: '+39', gb: '+44', us: '+1', ma: '+212', dz: '+213',
  tn: '+216', ae: '+971', sa: '+966', ro: '+40',
};
import { generateDefaultPseudo } from '@/utils/pseudoGenerator';
import { useBranding } from '@/contexts/BrandingContext';
import { getMobileOS, isApplePlatform } from '@/utils/mobileOsDetection';
import { palette, typography } from '@/styles/tokens';
import { AppleSignInButton } from '@/components/auth/AppleSignInButton';
import { GoogleSignInButton } from '@/components/auth/GoogleSignInButton';

type FlowStep = 'email' | 'otp' | 'marketing';

interface OTPSuccessData {
  isNewUser: boolean;
  userData: {
    id: string;
    smartuserId: string;
    email: string;
    phoneNumber: string;
    isSubscribed: boolean;
    pseudo: string;
  };
  sessionToken: string;
  identifier: string;
  identityType: 'EMAIL' | 'MSISDN';
}

interface DCBContext {
  dcbSessionToken: string;
  dcbAutologinToken: string;
  dcbTemporaryAbsoluteReference: string;
  identities?: Array<{ type: string; identifier: string }>;
}

export interface OTPLoginFlowHandle {
  requestClose: () => void;
}

interface OTPLoginFlowProps {
  onSuccessCallback?: () => void;
  onSuccessWithData?: (data: OTPSuccessData) => void;
  onClose?: () => void;
  showSocialLogin?: boolean;
  contextMessage?: string;
  titleOverride?: string;
  dcbContext?: DCBContext;
}

const createStyles = (theme: Theme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  stepContainer: {
    flex: 1,
    paddingHorizontal: 24,
    paddingBottom: 24,
    paddingTop: 8,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 24,
    marginLeft: -8,
  },
  backButtonText: {
    color: theme.colors.primary,
    fontSize: 17,
    marginLeft: 4,
  },
  title: {
    fontSize: 19,
    fontWeight: '600',
    color: palette.white,
    textAlign: 'center',
    marginBottom: 32,
  },
  contextMessage: {
    fontSize: 15,
    fontWeight: '400',
    color: theme.colors.textSecondary,
    textAlign: 'center',
    marginBottom: 8,
  },
  recoveryBanner: {
    backgroundColor: 'rgba(34, 197, 94, 0.12)',
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: 'rgba(34, 197, 94, 0.25)',
  },
  recoveryBannerText: {
    fontSize: 14,
    fontWeight: '500',
    color: palette.semantic.success,
    textAlign: 'center',
    lineHeight: 20,
  },
  msisdnRecoverySection: {
    marginTop: 24,
    alignItems: 'center' as const,
  },
  msisdnRecoveryLink: {
    fontSize: 14,
    color: theme.colors.primary,
    textDecorationLine: 'underline' as const,
    textAlign: 'center' as const,
  },
  msisdnRecoveryForm: {
    width: '100%' as const,
  },
  msisdnRecoveryLabel: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    marginBottom: 8,
  },
  msisdnRecoveryErrorText: {
    fontSize: 13,
    color: palette.semantic.error,
    marginBottom: 8,
    marginTop: -14,
  },
  msisdnRecoveryButtons: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: 12,
  },
  msisdnRecoveryCancelButton: {
    paddingVertical: 14,
    paddingHorizontal: 16,
  },
  msisdnRecoveryCancelText: {
    fontSize: 15,
    color: theme.colors.textSecondary,
  },
  inputContainer: {
    marginBottom: 22,
  },
  input: {
    backgroundColor: theme.colors.inputBackground,
    borderRadius: 11,
    padding: 16,
    fontSize: 15,
    color: theme.colors.inputText,
    borderWidth: 1,
    borderColor: theme.colors.inputBorder,
  },
  primaryButton: {
    backgroundColor: theme.colors.primary,
    borderRadius: 11,
    padding: 16,
    alignItems: 'center',
    marginTop: 24,
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  primaryButtonText: {
    color: palette.white,
    fontSize: 15,
    fontWeight: typography.weight.semibold,
  },
  dividerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 22,
  },
  divider: {
    flex: 1,
    height: 1,
    backgroundColor: theme.colors.divider,
  },
  dividerText: {
    color: theme.colors.textSecondary,
    fontSize: 15,
    marginHorizontal: 16,
  },
  socialButton: {
    backgroundColor: theme.colors.socialButtonBackground,
    borderRadius: 11,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
    borderWidth: 1,
    borderColor: theme.colors.socialButtonBorder,
    gap: 12,
  },
  appleButtonPrimary: {
    backgroundColor: palette.black,
    borderRadius: 11,
    paddingVertical: 14,
    paddingHorizontal: 18,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 0,
    borderWidth: 1,
    borderColor: 'transparent',
    gap: 10,
  },
  appleIconPrimary: {
    fontSize: 18,
    color: palette.white,
  },
  appleButtonTextPrimary: {
    color: palette.white,
    fontSize: 14,
    fontWeight: typography.weight.semibold,
  },
  googleButtonIOS: {
    backgroundColor: palette.white,
    borderRadius: 11,
    paddingVertical: 14,
    paddingHorizontal: 18,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 0,
    borderWidth: 1,
    borderColor: palette.white,
    gap: 10,
  },
  googleIconIOS: {
    width: 18,
    height: 18,
  },
  googleButtonTextIOS: {
    color: palette.black,
    fontSize: 14,
    fontWeight: typography.weight.semibold,
  },
  socialButtonsDivider: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 12,
  },
  socialIcon: {
    width: 23,
    height: 23,
  },
  appleIcon: {
    fontSize: 24,
  },
  socialButtonText: {
    color: theme.colors.socialButtonText,
    fontSize: 15,
    fontWeight: '600',
  },
  otpTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: theme.colors.textPrimary,
    marginBottom: 12,
  },
  otpSubtitle: {
    fontSize: 16,
    color: theme.colors.textMuted,
    lineHeight: 24,
    marginBottom: 32,
  },
  emailBold: {
    fontWeight: '700',
    color: theme.colors.textPrimary,
  },
  otpContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    width: '100%',
    marginBottom: 24,
    gap: 8,
  },
  otpInput: {
    flex: 1,
    maxWidth: 50,
    height: 54,
    backgroundColor: theme.colors.inputBackground,
    borderRadius: 11,
    borderWidth: 1,
    borderColor: theme.colors.inputBorder,
    textAlign: 'center',
    fontSize: 22,
    fontWeight: '600',
    color: theme.colors.inputText,
  },
  otpInputFilled: {
    borderColor: theme.colors.primary,
    borderWidth: 2,
  },
  resendButton: {
    alignSelf: 'flex-start',
    marginBottom: 24,
  },
  resendButtonText: {
    color: theme.colors.primary,
    fontSize: 16,
    fontWeight: '500',
  },
  resendDisabled: {
    color: theme.colors.textSecondary,
  },
  errorContainer: {
    backgroundColor: theme.colors.error,
    borderRadius: 10,
    padding: 14,
    marginBottom: 22,
    borderLeftWidth: 4,
    borderLeftColor: theme.colors.errorAccent,
  },
  errorText: {
    color: palette.white,
    fontSize: 15,
    fontWeight: typography.weight.medium,
    textAlign: 'center',
  },
  marketingHeader: {
    alignItems: 'center',
    marginBottom: 24,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.divider,
  },
  accountCreatedText: {
    color: theme.colors.textSecondary,
    fontSize: 16,
    fontWeight: '500',
  },
  heartContainer: {
    alignItems: 'center',
    marginBottom: 24,
  },
  heartCircle: {
    width: 80,
    height: 80,
    borderRadius: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  marketingTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: theme.colors.textPrimary,
    textAlign: 'center',
    marginBottom: 16,
    lineHeight: 32,
  },
  marketingDescription: {
    fontSize: 16,
    color: theme.colors.textMuted,
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 16,
    paddingHorizontal: 16,
  },
  declineButton: {
    alignSelf: 'center',
    marginTop: 16,
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: theme.colors.primary,
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 20,
  },
  declineButtonText: {
    color: theme.colors.primary,
    fontSize: 15,
    fontWeight: '500',
  },
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 32,
    gap: 16,
  },
  loadingText: {
    fontSize: 16,
    fontWeight: '500',
    color: theme.colors.textSecondary,
  },
  biometricButton: {
    backgroundColor: theme.colors.socialButtonBackground,
    borderRadius: 12,
    padding: 18,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 12,
    borderWidth: 1,
    borderColor: theme.colors.socialButtonBorder,
    gap: 12,
  },
  biometricButtonText: {
    color: theme.colors.socialButtonText,
    fontSize: 17,
    fontWeight: '600',
  },
  emailWarning: {
    color: theme.colors.textSecondary,
    fontSize: 13,
    fontStyle: 'italic',
    textAlign: 'center',
    marginTop: 12,
  },
});

export const OTPLoginFlow = forwardRef<OTPLoginFlowHandle, OTPLoginFlowProps>(function OTPLoginFlow({
  onSuccessCallback,
  onSuccessWithData,
  onClose,
  showSocialLogin: showSocialLoginProp = true,
  contextMessage,
  titleOverride,
  dcbContext
}, ref) {
  const { isAuthMethodEnabled } = useBranding();
  const showSocialLogin = showSocialLoginProp;
  const [currentStep, setCurrentStep] = useState<FlowStep>('email');
  const [emailOrPhone, setEmailOrPhone] = useState('');
  const [temporaryToken, setTemporaryToken] = useState('');
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isNewUser, setIsNewUser] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [isAppleSignInAvailable, setIsAppleSignInAvailable] = useState(false);
  const [appleAuthModule, setAppleAuthModule] = useState<typeof import('expo-apple-authentication') | null>(null);
  const [isGoogleSignInAvailable, setIsGoogleSignInAvailable] = useState(false);
  const [biometricSupport, setBiometricSupport] = useState<{ isAvailable: boolean; isEnrolled: boolean; supportedTypes: string[] }>({
    isAvailable: false,
    isEnrolled: false,
    supportedTypes: []
  });
  const [isBiometricEnabled, setIsBiometricEnabled] = useState(false);
  const [biometricAutoLoginAttempted, setBiometricAutoLoginAttempted] = useState(false);
  const [recoveredDcbContext, setRecoveredDcbContext] = useState<DCBContext | null>(null);
  const [dcbRecoveryBanner, setDcbRecoveryBanner] = useState(false);
  const [showMsisdnRecovery, setShowMsisdnRecovery] = useState(false);
  const [msisdnRecoveryInput, setMsisdnRecoveryInput] = useState('');
  const [msisdnRecoveryLoading, setMsisdnRecoveryLoading] = useState(false);
  const [msisdnRecoveryError, setMsisdnRecoveryError] = useState('');
  const [countryDialCode, setCountryDialCode] = useState('+33');
  const [pendingLoginData, setPendingLoginData] = useState<{
    user: {
      id: string;
      smartuserId: string;
      email: string;
      phoneNumber?: string;
      phone_number?: string;
      isSubscribed: boolean;
      pseudo?: string;
    };
    sessionToken: string;
    isNewUser: boolean;
    authMethod: 'otp' | 'apple' | 'google';
  } | null>(null);
  const isMountedRef = useRef(true);
  const onSuccessCallbackRef = useRef(onSuccessCallback);
  const onSuccessWithDataRef = useRef(onSuccessWithData);

  const otpInputRefs = useRef<(TextInput | null)[]>([]);

  const { signInWithApple, signInWithGoogle, completeSocialAuthAfterMarketing, loginWithOTPData, checkBiometricSupport, performBiometricLogin, isBiometricEnabled: checkIsBiometricEnabled } = useAuth();
  const { t, language: interfaceLanguage } = useTranslation();
  const theme = useTheme();
  const forcedDarkTheme: Theme = useMemo(() => ({
    ...darkTheme,
    colors: {
      ...darkTheme.colors,
      primary: theme.colors.primary,
      inputBorderFocused: theme.colors.primary,
      tabBarActiveTint: theme.colors.primary,
    },
  }), [theme.colors.primary]);
  const styles = useMemo(() => createStyles(forcedDarkTheme), [forcedDarkTheme]);

  useEffect(() => {
    onSuccessCallbackRef.current = onSuccessCallback;
    onSuccessWithDataRef.current = onSuccessWithData;
  }, [onSuccessCallback, onSuccessWithData]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    persistentCountryCache.get().then((data) => {
      const code = (data?.countryCode || 'fr').toLowerCase();
      const dial = COUNTRY_DIAL_CODES[code] || '+33';
      setCountryDialCode(dial);
    });
  }, []);

  useEffect(() => {
    if (dcbContext || Platform.OS !== 'web') return;

    const DCB_LS_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000;

    const applyRecoveredData = (data: any, source: string) => {
      if (data.found && data.sessionToken && data.temporaryAbsoluteReference) {
        console.log(`[OTPLoginFlow] Recovered pending DCB subscription from ${source}`);
        setRecoveredDcbContext({
          dcbSessionToken: data.sessionToken,
          dcbAutologinToken: data.autologinToken || '',
          dcbTemporaryAbsoluteReference: data.temporaryAbsoluteReference,
          identities: Array.isArray(data.identities) ? data.identities : [],
        });
        setDcbRecoveryBanner(true);
        return true;
      }
      return false;
    };

    const recoverDcb = async () => {
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY}`,
      };

      let storedOperationId: string | null = null;
      try {
        const stored = localStorage.getItem('dcb_last_operation_id');
        const storedAt = localStorage.getItem('dcb_operation_id_stored_at');
        if (stored && storedAt) {
          const age = Date.now() - Number(storedAt);
          if (age < DCB_LS_EXPIRY_MS) {
            storedOperationId = stored;
          } else {
            localStorage.removeItem('dcb_last_operation_id');
            localStorage.removeItem('dcb_operation_id_stored_at');
          }
        }
      } catch {}

      if (storedOperationId) {
        try {
          const res = await fetch(`${supabaseUrl}/functions/v1/manage-users`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ action: 'dcb_recover_by_operation_id', operationId: storedOperationId }),
          });
          const data = await res.json();
          if (applyRecoveredData(data, 'localStorage operationId')) return;
        } catch (err) {
          console.log('[OTPLoginFlow] localStorage operationId recovery failed', err);
        }
      }

      try {
        const res = await fetch(`${supabaseUrl}/functions/v1/manage-users`, {
          method: 'POST',
          credentials: 'include',
          headers,
          body: JSON.stringify({ action: 'dcb_recover_pending' }),
        });
        const data = await res.json();
        if (applyRecoveredData(data, 'cookie')) return;
      } catch (err) {
        console.log('[OTPLoginFlow] Cookie recovery check failed silently', err);
      }
    };

    recoverDcb();
  }, [dcbContext]);

  const effectiveDcbContext = dcbContext || recoveredDcbContext;

  useEffect(() => {
    async function checkAppleSignIn() {
      if (Platform.OS === 'web') {
        if (isApplePlatform()) {
          setIsAppleSignInAvailable(true);
        }
        return;
      }
      if (Platform.OS !== 'ios') return;
      try {
        const module = await import('expo-apple-authentication');
        setAppleAuthModule(module);
        const available = await module.isAvailableAsync();
        setIsAppleSignInAvailable(available);
      } catch (error) {
        console.error('Error loading Apple Sign In module:', error);
      }
    }
    checkAppleSignIn();
  }, []);

  useEffect(() => {
    if (Platform.OS === 'android' || Platform.OS === 'ios' || Platform.OS === 'web') {
      setIsGoogleSignInAvailable(true);
    }
  }, []);

  // Check biometric support and auto-login if enabled
  useEffect(() => {
    console.log('🔐 [OTPLoginFlow] Component mounted, checking biometric support...');

    const checkBiometricsAndAutoLogin = async () => {
      if (Platform.OS === 'ios' || Platform.OS === 'android' || Platform.OS === 'web') {
        console.log('🔐 [OTPLoginFlow] Platform compatible, checking biometric support...');

        const support = await checkBiometricSupport();
        console.log('🔐 [OTPLoginFlow] Biometric support result:', {
          isAvailable: support.isAvailable,
          isEnrolled: support.isEnrolled,
          supportedTypes: support.supportedTypes
        });

        setBiometricSupport(support);

        if (support.isAvailable && support.isEnrolled) {
          console.log('🔐 [OTPLoginFlow] Biometric available and enrolled, checking if enabled...');

          const enabled = await checkIsBiometricEnabled();
          console.log('🔐 [OTPLoginFlow] Biometric enabled status:', enabled);

          setIsBiometricEnabled(enabled);

          // Auto-login if biometric is enabled and not already attempted
          if (enabled && !biometricAutoLoginAttempted) {
            console.log('🔐 [OTPLoginFlow] Biometric enabled - auto-launching biometric login');
            setBiometricAutoLoginAttempted(true);

            // Small delay to ensure UI is ready
            setTimeout(async () => {
              console.log('🔐 [OTPLoginFlow] Triggering biometric login...');
              await handleBiometricLogin();
            }, 300);
          } else if (!enabled) {
            console.log('🔐 [OTPLoginFlow] Biometric not enabled - user must enable it manually');
          } else if (biometricAutoLoginAttempted) {
            console.log('🔐 [OTPLoginFlow] Biometric auto-login already attempted - skipping');
          }
        } else {
          console.log('🔐 [OTPLoginFlow] Biometric not available or not enrolled:', {
            available: support.isAvailable,
            enrolled: support.isEnrolled
          });
        }
      } else {
        console.log('🔐 [OTPLoginFlow] Platform not compatible for biometric:', Platform.OS);
      }
    };

    checkBiometricsAndAutoLogin();
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleBiometricLogin = async () => {
    console.log('🔐 [OTPLoginFlow] handleBiometricLogin called');
    setErrorMessage('');
    setIsLoading(true);

    try {
      console.log('🔐 [OTPLoginFlow] Calling performBiometricLogin...');
      const result = await performBiometricLogin();
      console.log('🔐 [OTPLoginFlow] performBiometricLogin result:', {
        success: result.success,
        error: result.error
      });

      if (result.success) {
        console.log('🔐 [OTPLoginFlow] Biometric login successful!');

        // Call success callback
        if (onSuccessCallbackRef.current) {
          console.log('🔐 [OTPLoginFlow] Calling onSuccessCallback...');
          onSuccessCallbackRef.current();
        } else {
          console.log('⚠️ [OTPLoginFlow] onSuccessCallback is not defined');
        }
      } else {
        console.error('❌ [OTPLoginFlow] Biometric login failed:', result.error);
        if (result.error !== 'CANCELLED') {
          setErrorMessage(result.error || t('biometricAuthFailed'));
        } else {
          console.log('🔐 [OTPLoginFlow] Biometric login cancelled by user');
        }
      }
    } catch (error) {
      console.error('❌ [OTPLoginFlow] Error during biometric login:', error);
      setErrorMessage(t('biometricAuthFailed'));
    } finally {
      setIsLoading(false);
      console.log('🔐 [OTPLoginFlow] Biometric login process completed');
    }
  };

  const handleSendOTP = async () => {
    setErrorMessage('');

    if (!emailOrPhone) {
      setErrorMessage(t('otp.enterEmailOrPhone'));
      return;
    }

    let finalEmailOrPhone = emailOrPhone.trim();
    const isPhone = isPhoneNumber(finalEmailOrPhone);

    if (isPhone && !isAuthMethodEnabled('msisdn')) {
      setErrorMessage(t('otp.emailPlaceholder'));
      return;
    }

    if (!isPhone && !isAuthMethodEnabled('email')) {
      setErrorMessage(t('otp.phonePlaceholder'));
      return;
    }

    if (isPhone) {
      const phoneValidation = validatePhoneNumber(finalEmailOrPhone);
      if (!phoneValidation.isValid) {
        setErrorMessage(t('invalidPhoneFormat'));
        return;
      }
      finalEmailOrPhone = cleanPhoneNumber(finalEmailOrPhone);
    } else {
      const emailValidation = validateEmail(finalEmailOrPhone);
      if (!emailValidation.isValid) {
        setErrorMessage(t('invalidEmailFormat'));
        return;
      }
    }

    setIsLoading(true);

    try {
      const countryData = await persistentCountryCache.get();
      const countryCode = (countryData?.countryCode || 'FR').toUpperCase();

      const requestBody: Record<string, string> = {
        action: 'send_otp',
        countryCode,
        language: interfaceLanguage,
      };

      if (isPhone) {
        requestBody.phoneNumber = finalEmailOrPhone;
        console.log('[OTPLoginFlow] Sending SMS OTP with country code:', countryCode);
      } else {
        requestBody.email = finalEmailOrPhone;
        console.log('[OTPLoginFlow] Sending Email OTP');
      }

      if (effectiveDcbContext) {
        requestBody.dcbSessionToken = effectiveDcbContext.dcbSessionToken;
        requestBody.dcbAutologinToken = effectiveDcbContext.dcbAutologinToken;
        requestBody.dcbTemporaryAbsoluteReference = effectiveDcbContext.dcbTemporaryAbsoluteReference;
        if (effectiveDcbContext.identities && effectiveDcbContext.identities.length > 0) {
          requestBody.dcbIdentities = JSON.stringify(effectiveDcbContext.identities);
        }
        console.log('[OTPLoginFlow] Including DCB context in send_otp request', { identitiesCount: effectiveDcbContext.identities?.length || 0 });
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/manage-users`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        if (data.error === 'identity_mismatch') {
          const masked = data.expectedIdentifier || '***';
          const msgKey = data.expectedType === 'MSISDN' ? 'otp.dcbIdentityMismatchPhone' : 'otp.dcbIdentityMismatchEmail';
          setErrorMessage(t(msgKey, { identifier: masked }));
        } else {
          setErrorMessage(data.message || data.error || t('otp.sendFailed'));
        }
        return;
      }

      setEmailOrPhone(finalEmailOrPhone);
      setTemporaryToken(data.temporaryToken);
      setCountdown(60);
      setCurrentStep('otp');
      setOtpCode(['', '', '', '', '', '']);
    } catch (error) {
      console.error('Error sending OTP:', error);
      setErrorMessage(t('otp.sendFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async (codeOverride?: string) => {
    const code = codeOverride || otpCode.join('');
    if (code.length !== 6) {
      setErrorMessage(t('otp.enterCompleteCode'));
      return;
    }

    setErrorMessage('');
    setIsLoading(true);

    try {
      const countryData = await persistentCountryCache.get();
      const countryCode = (countryData?.countryCode || 'FR').toUpperCase();

      const verifyBody: Record<string, string> = {
        action: 'verify_otp',
        temporaryToken,
        code,
        countryCode,
      };

      if (effectiveDcbContext) {
        verifyBody.dcbSessionToken = effectiveDcbContext.dcbSessionToken;
        verifyBody.dcbAutologinToken = effectiveDcbContext.dcbAutologinToken;
        verifyBody.dcbTemporaryAbsoluteReference = effectiveDcbContext.dcbTemporaryAbsoluteReference;
        if (effectiveDcbContext.identities && effectiveDcbContext.identities.length > 0) {
          verifyBody.dcbIdentities = JSON.stringify(effectiveDcbContext.identities);
        }
        console.log('[OTPLoginFlow] Including DCB context in verify_otp request', { identitiesCount: effectiveDcbContext.identities?.length || 0 });
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/manage-users`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify(verifyBody),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setErrorMessage(data.error || t('otp.verificationFailed'));
        return;
      }

      console.log('[OTPLoginFlow] OTP verified, processing login data...');
      console.log('[OTPLoginFlow] needsMarketingConsent:', data.needsMarketingConsent);
      setIsNewUser(data.isNewUser || false);

      const userData = {
        id: data.user.id || data.user.smartuserId,
        smartuserId: data.user.smartuserId,
        email: data.user.email || '',
        phoneNumber: data.user.phoneNumber || '',
        isSubscribed: data.user.isSubscribed || false,
        pseudo: data.user.pseudo,
        paymentMethod: data.user.paymentMethod || null,
      };

      if (data.needsMarketingConsent) {
        console.log('[OTPLoginFlow] Marketing consent required, deferring login...');
        setPendingLoginData({
          user: userData,
          sessionToken: data.sessionToken,
          isNewUser: data.isNewUser || false,
          authMethod: 'otp',
        });
        setCurrentStep('marketing');
      } else {
        const loginResult = await loginWithOTPData(
          userData,
          data.sessionToken,
          data.isNewUser || false
        );

        if (!loginResult.success) {
          setErrorMessage(loginResult.error || t('otp.verificationFailed'));
          return;
        }

        console.log('[OTPLoginFlow] Login successful, user authenticated');

        if (onSuccessWithDataRef.current) {
          const identityType: 'EMAIL' | 'MSISDN' = isPhoneNumber(emailOrPhone) ? 'MSISDN' : 'EMAIL';
          onSuccessWithDataRef.current({
            isNewUser: data.isNewUser || false,
            userData,
            sessionToken: data.sessionToken,
            identifier: emailOrPhone,
            identityType,
          });
        }
        handleAuthSuccess();
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      setErrorMessage(t('otp.verificationFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarketingConsent = async (consent: boolean) => {
    setIsLoading(true);

    try {
      console.log('[OTPLoginFlow] Processing marketing consent:', consent);
      console.log('[OTPLoginFlow] pendingLoginData:', pendingLoginData?.authMethod);

      if (pendingLoginData) {
        console.log('[OTPLoginFlow] Completing deferred login for method:', pendingLoginData.authMethod);

        if (pendingLoginData.authMethod === 'otp') {
          console.log('[OTPLoginFlow] Updating marketing consent for smartuserId:', pendingLoginData.user.smartuserId);

          await fetch(`${supabaseUrl}/functions/v1/update-marketing-consent`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({
              smartuserId: pendingLoginData.user.smartuserId,
              consent,
            }),
          });

          const loginResult = await loginWithOTPData(
            {
              id: pendingLoginData.user.id,
              smartuserId: pendingLoginData.user.smartuserId,
              email: pendingLoginData.user.email,
              phoneNumber: pendingLoginData.user.phoneNumber || pendingLoginData.user.phone_number || '',
              isSubscribed: pendingLoginData.user.isSubscribed,
              pseudo: pendingLoginData.user.pseudo,
              paymentMethod: pendingLoginData.user.paymentMethod || null,
            },
            pendingLoginData.sessionToken,
            pendingLoginData.isNewUser,
            consent
          );

          if (!loginResult.success) {
            console.error('[OTPLoginFlow] Deferred OTP login failed:', loginResult.error);
          } else {
            console.log('[OTPLoginFlow] Deferred OTP login successful');
          }
        } else if (pendingLoginData.authMethod === 'apple' || pendingLoginData.authMethod === 'google') {
          console.log('[OTPLoginFlow] Completing social login for:', pendingLoginData.authMethod);

          await fetch(`${supabaseUrl}/functions/v1/update-marketing-consent`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({
              smartuserId: pendingLoginData.user.smartuserId,
              consent,
            }),
          });

          const socialLoginResult = await completeSocialAuthAfterMarketing(
            {
              user: {
                id: pendingLoginData.user.id,
                email: pendingLoginData.user.email,
                phone_number: pendingLoginData.user.phone_number || '',
                pseudo: pendingLoginData.user.pseudo || generateDefaultPseudo(pendingLoginData.user.smartuserId),
                isSubscribed: pendingLoginData.user.isSubscribed,
                smartuserId: pendingLoginData.user.smartuserId,
              },
              sessionToken: pendingLoginData.sessionToken,
              isNewUser: pendingLoginData.isNewUser,
            },
            pendingLoginData.authMethod,
            consent
          );

          if (!socialLoginResult.success) {
            console.error(`[OTPLoginFlow] Deferred ${pendingLoginData.authMethod} login failed:`, socialLoginResult.error);
          } else {
            console.log(`[OTPLoginFlow] Deferred ${pendingLoginData.authMethod} login successful`);
          }
        }

        if (onSuccessWithDataRef.current && pendingLoginData.authMethod === 'otp') {
          const identityType: 'EMAIL' | 'MSISDN' = isPhoneNumber(emailOrPhone) ? 'MSISDN' : 'EMAIL';
          onSuccessWithDataRef.current({
            isNewUser: pendingLoginData.isNewUser,
            userData: {
              id: pendingLoginData.user.id,
              smartuserId: pendingLoginData.user.smartuserId,
              email: pendingLoginData.user.email,
              phoneNumber: pendingLoginData.user.phoneNumber || pendingLoginData.user.phone_number || '',
              isSubscribed: pendingLoginData.user.isSubscribed,
              pseudo: pendingLoginData.user.pseudo || '',
            },
            sessionToken: pendingLoginData.sessionToken,
            identifier: emailOrPhone,
            identityType,
          });
        }

        setPendingLoginData(null);
      }

      handleAuthSuccess();
    } catch (error) {
      console.error('Error updating marketing consent:', error);
      if (pendingLoginData) {
        if (pendingLoginData.authMethod === 'otp') {
          await loginWithOTPData(
            {
              id: pendingLoginData.user.id,
              smartuserId: pendingLoginData.user.smartuserId,
              email: pendingLoginData.user.email,
              phoneNumber: pendingLoginData.user.phoneNumber || pendingLoginData.user.phone_number || '',
              isSubscribed: pendingLoginData.user.isSubscribed,
              pseudo: pendingLoginData.user.pseudo,
            },
            pendingLoginData.sessionToken,
            pendingLoginData.isNewUser,
            consent
          );
        } else if (pendingLoginData.authMethod === 'apple' || pendingLoginData.authMethod === 'google') {
          await completeSocialAuthAfterMarketing(
            {
              user: {
                id: pendingLoginData.user.id,
                email: pendingLoginData.user.email,
                phone_number: pendingLoginData.user.phone_number || '',
                pseudo: pendingLoginData.user.pseudo || generateDefaultPseudo(pendingLoginData.user.smartuserId),
                isSubscribed: pendingLoginData.user.isSubscribed,
                smartuserId: pendingLoginData.user.smartuserId,
              },
              sessionToken: pendingLoginData.sessionToken,
              isNewUser: pendingLoginData.isNewUser,
            },
            pendingLoginData.authMethod,
            consent
          );
        }
        setPendingLoginData(null);
      }
      handleAuthSuccess();
    } finally {
      setIsLoading(false);
    }
  };

  const handleAuthSuccess = () => {
    if (onSuccessCallbackRef.current) {
      onSuccessCallbackRef.current();
    }
  };

  const handleResendOTP = async () => {
    if (countdown > 0) return;
    setOtpCode(['', '', '', '', '', '']);
    setCurrentStep('email');
    setTimeout(() => handleSendOTP(), 100);
  };

  const handleOTPChange = (index: number, value: string) => {
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newOTP = [...otpCode];
      digits.forEach((digit, i) => {
        if (index + i < 6) {
          newOTP[index + i] = digit;
        }
      });
      setOtpCode(newOTP);
      const nextIndex = Math.min(index + digits.length, 5);
      otpInputRefs.current[nextIndex]?.focus();

      if (newOTP.every(d => d !== '') && !isLoading) {
        const completeCode = newOTP.join('');
        setTimeout(() => handleVerifyOTP(completeCode), 100);
      }
      return;
    }

    const digit = value.replace(/\D/g, '');
    const newOTP = [...otpCode];
    newOTP[index] = digit;
    setOtpCode(newOTP);

    if (digit && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }

    if (newOTP.every(d => d !== '') && !isLoading) {
      const completeCode = newOTP.join('');
      setTimeout(() => handleVerifyOTP(completeCode), 100);
    }
  };

  const handleOTPKeyPress = (index: number, key: string) => {
    if (key === 'Backspace' && !otpCode[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleAppleSignIn = async () => {
    setErrorMessage('');
    setIsLoading(true);

    try {
      const result = await signInWithApple();

      if (result.success) {
        if (result.needsMarketingConsent && result.pendingAuthData) {
          console.log('[OTPLoginFlow] Apple Sign In: Marketing consent required, showing marketing screen...');
          setPendingLoginData({
            user: {
              id: result.pendingAuthData.user.id,
              smartuserId: result.pendingAuthData.user.smartuserId,
              email: result.pendingAuthData.user.email || '',
              phone_number: result.pendingAuthData.user.phone_number || '',
              isSubscribed: result.pendingAuthData.user.isSubscribed,
              pseudo: result.pendingAuthData.user.pseudo,
            },
            sessionToken: result.pendingAuthData.sessionToken,
            isNewUser: result.pendingAuthData.isNewUser,
            authMethod: 'apple',
          });
          setIsNewUser(result.isNewUser || false);
          setCurrentStep('marketing');
          setIsLoading(false);
        } else {
          appStabilityGuard.setRecoveryInProgress(true);

          if (Platform.OS === 'ios') {
            InteractionManager.runAfterInteractions(() => {
              setTimeout(() => {
                if (isMountedRef.current && onSuccessCallbackRef.current) {
                  onSuccessCallbackRef.current();
                } else {
                  appStabilityGuard.setRecoveryInProgress(false);
                }
              }, 500);
            });
          } else {
            setTimeout(() => {
              if (onSuccessCallbackRef.current) {
                onSuccessCallbackRef.current();
              } else {
                appStabilityGuard.setRecoveryInProgress(false);
              }
            }, 0);
          }
        }
      } else if (result.error && result.error !== 'CANCELLED') {
        setErrorMessage(result.error);
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      setErrorMessage(t('appleSignInFailed'));
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setErrorMessage('');
    setIsLoading(true);

    try {
      const result = await signInWithGoogle();

      if (result.success) {
        if (result.needsMarketingConsent && result.pendingAuthData) {
          console.log('[OTPLoginFlow] Google Sign In: Marketing consent required, showing marketing screen...');
          setPendingLoginData({
            user: {
              id: result.pendingAuthData.user.id,
              smartuserId: result.pendingAuthData.user.smartuserId,
              email: result.pendingAuthData.user.email || '',
              phone_number: result.pendingAuthData.user.phone_number || '',
              isSubscribed: result.pendingAuthData.user.isSubscribed,
              pseudo: result.pendingAuthData.user.pseudo,
            },
            sessionToken: result.pendingAuthData.sessionToken,
            isNewUser: result.pendingAuthData.isNewUser,
            authMethod: 'google',
          });
          setIsNewUser(result.isNewUser || false);
          setCurrentStep('marketing');
          setIsLoading(false);
        } else {
          if (onSuccessCallback) {
            onSuccessCallback();
          }
        }
      } else if (result.error && result.error !== 'CANCELLED') {
        setErrorMessage(result.error);
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      setErrorMessage(t('googleSignInFailed'));
      setIsLoading(false);
    }
  };

  const handleExternalClose = useCallback(() => {
    if (currentStep === 'marketing' && pendingLoginData && !isLoading) {
      handleMarketingConsent(false);
    } else if (onClose) {
      onClose();
    }
  }, [currentStep, pendingLoginData, isLoading, handleMarketingConsent, onClose]);

  useImperativeHandle(ref, () => ({
    requestClose: handleExternalClose,
  }), [handleExternalClose]);

  const handleBack = () => {
    if (currentStep === 'otp') {
      setCurrentStep('email');
      setOtpCode(['', '', '', '', '', '']);
      setErrorMessage('');
    } else if (currentStep === 'marketing') {
      handleExternalClose();
    } else if (onClose) {
      onClose();
    }
  };

  const AppleAuthenticationButton = appleAuthModule?.AppleAuthenticationButton;

  const handleMsisdnRecovery = async () => {
    if (!msisdnRecoveryInput || msisdnRecoveryInput.length < 8) {
      setMsisdnRecoveryError(t('otp.invalidPhoneNumber'));
      return;
    }
    setMsisdnRecoveryLoading(true);
    setMsisdnRecoveryError('');
    try {
      const res = await fetch(`${supabaseUrl}/functions/v1/manage-users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ action: 'dcb_recover_by_msisdn', msisdn: msisdnRecoveryInput }),
      });
      const data = await res.json();
      if (data.found && data.sessionToken && data.temporaryAbsoluteReference) {
        console.log('[OTPLoginFlow] Recovered pending DCB subscription via MSISDN');
        setRecoveredDcbContext({
          dcbSessionToken: data.sessionToken,
          dcbAutologinToken: data.autologinToken || '',
          dcbTemporaryAbsoluteReference: data.temporaryAbsoluteReference,
          identities: Array.isArray(data.identities) ? data.identities : [],
        });
        setDcbRecoveryBanner(true);
        setShowMsisdnRecovery(false);
      } else {
        setMsisdnRecoveryError(t('otp.dcbMsisdnNotFound'));
      }
    } catch {
      setMsisdnRecoveryError(t('otp.dcbMsisdnRecoveryFailed'));
    } finally {
      setMsisdnRecoveryLoading(false);
    }
  };

  const renderEmailStep = () => (
    <View style={styles.stepContainer}>
      <Text style={styles.title}>{titleOverride || t('otp.signInOrSignUp')}</Text>
      {contextMessage && (
        <Text style={styles.contextMessage}>{contextMessage}</Text>
      )}

      {dcbRecoveryBanner && !contextMessage && (
        <View style={styles.recoveryBanner}>
          <Text style={styles.recoveryBannerText}>{t('otp.dcbRecoveryBanner')}</Text>
        </View>
      )}

      {showSocialLogin && isAuthMethodEnabled('apple_signin') && isAppleSignInAvailable && (Platform.OS === 'web' || (appleAuthModule && AppleAuthenticationButton)) && (
        <AppleSignInButton onPress={handleAppleSignIn} disabled={isLoading} />
      )}

      {showSocialLogin && isAuthMethodEnabled('apple_signin') && isAppleSignInAvailable && isAuthMethodEnabled('google_signin') && isGoogleSignInAvailable && (Platform.OS === 'ios' || Platform.OS === 'web') && (
        <View style={styles.socialButtonsDivider}>
          <View style={styles.divider} />
          <Text style={styles.dividerText}>{t('or')}</Text>
          <View style={styles.divider} />
        </View>
      )}

      {showSocialLogin && isAuthMethodEnabled('google_signin') && isGoogleSignInAvailable && (Platform.OS === 'ios' || Platform.OS === 'web') && (
        <GoogleSignInButton onPress={handleGoogleSignIn} disabled={isLoading} />
      )}

      {showSocialLogin && (
        (isAuthMethodEnabled('apple_signin') && isAppleSignInAvailable) ||
        (isAuthMethodEnabled('google_signin') && isGoogleSignInAvailable && (Platform.OS === 'ios' || Platform.OS === 'web'))
      ) && (isAuthMethodEnabled('email') || isAuthMethodEnabled('msisdn')) && (
        <View style={styles.dividerContainer}>
          <View style={styles.divider} />
          <Text style={styles.dividerText}>{t('or')}</Text>
          <View style={styles.divider} />
        </View>
      )}

      {(isAuthMethodEnabled('email') || isAuthMethodEnabled('msisdn')) && (
        <View style={styles.inputContainer}>
          <TextInput
            style={styles.input}
            value={emailOrPhone}
            onChangeText={(text) => {
              setEmailOrPhone(text);
              setErrorMessage('');
            }}
            placeholder={
              isAuthMethodEnabled('email') && isAuthMethodEnabled('msisdn')
                ? t('otp.emailOrPhonePlaceholder')
                : isAuthMethodEnabled('email')
                  ? t('otp.emailPlaceholder')
                  : t('otp.phonePlaceholder')
            }
            placeholderTextColor={forcedDarkTheme.colors.placeholder}
            keyboardType={!isAuthMethodEnabled('email') ? 'phone-pad' : 'email-address'}
            autoCapitalize="none"
            autoCorrect={false}
            autoComplete={isAuthMethodEnabled('email') ? 'email' : 'tel'}
            returnKeyType="done"
            onSubmitEditing={handleSendOTP}
          />
        </View>
      )}

      {errorMessage ? (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{errorMessage}</Text>
        </View>
      ) : null}

      {(isAuthMethodEnabled('email') || isAuthMethodEnabled('msisdn')) && (
        <TouchableOpacity
          style={[styles.primaryButton, isLoading && styles.buttonDisabled]}
          onPress={handleSendOTP}
          disabled={isLoading}
        >
          {isLoading ? (
            <ActivityIndicator color="#ffffff" />
          ) : (
            <Text style={styles.primaryButtonText}>{t('otp.continue')}</Text>
          )}
        </TouchableOpacity>
      )}

      {showSocialLogin && isAuthMethodEnabled('google_signin') && isGoogleSignInAvailable && Platform.OS === 'android' && (
        <>
          <View style={styles.dividerContainer}>
            <View style={styles.divider} />
            <Text style={styles.dividerText}>{t('or')}</Text>
            <View style={styles.divider} />
          </View>

          <GoogleSignInButton onPress={handleGoogleSignIn} disabled={isLoading} />
        </>
      )}


    </View>
  );

  const renderOTPStep = () => {
    const isPhone = isPhoneNumber(emailOrPhone);
    return (
    <View style={styles.stepContainer}>
      <TouchableOpacity style={styles.backButton} onPress={handleBack}>
        <ChevronLeft size={24} color={forcedDarkTheme.colors.primary} />
        <Text style={styles.backButtonText}>{isPhone ? t('otp.confirmPhone') : t('otp.confirmEmail')}</Text>
      </TouchableOpacity>

      <Text style={styles.otpTitle}>{isPhone ? t('otp.confirmYourPhone') : t('otp.confirmYourEmail')}</Text>
      <Text style={styles.otpSubtitle}>
        {isPhone ? t('otp.codeSentToPhone') : t('otp.codeSentTo')} <Text style={styles.emailBold}>{emailOrPhone}</Text>.
        {'\n'}{t('otp.enterCodeBelow')}
      </Text>

      <View style={styles.otpContainer}>
        {otpCode.map((digit, index) => (
          <TextInput
            key={index}
            ref={(ref) => { otpInputRefs.current[index] = ref; }}
            style={[styles.otpInput, digit ? styles.otpInputFilled : null]}
            value={digit}
            onChangeText={(value) => handleOTPChange(index, value)}
            onKeyPress={({ nativeEvent }) => handleOTPKeyPress(index, nativeEvent.key)}
            keyboardType="number-pad"
            maxLength={index === 0 ? 6 : 1}
            selectTextOnFocus
            autoFocus={index === 0}
            textContentType={index === 0 ? 'oneTimeCode' : 'none'}
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
          />
        ))}
      </View>

      {errorMessage ? (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{errorMessage}</Text>
        </View>
      ) : null}

      {isLoading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={forcedDarkTheme.colors.primary} />
          <Text style={styles.loadingText}>{t('otp.connectingInProgress')}</Text>
        </View>
      ) : (
        <>
          <TouchableOpacity
            style={styles.resendButton}
            onPress={handleResendOTP}
            disabled={countdown > 0}
          >
            <Text style={[styles.resendButtonText, countdown > 0 && styles.resendDisabled]}>
              {countdown > 0 ? `${t('otp.resendIn')} ${countdown}s` : t('otp.resendCode')}
            </Text>
          </TouchableOpacity>

          {!isPhone && (
            <Text style={styles.emailWarning}>{t('otp.checkSpamFolder')}</Text>
          )}
        </>
      )}
    </View>
  );
  };

  const renderMarketingStep = () => (
    <View style={styles.stepContainer}>
      <View style={styles.marketingHeader}>
        <Text style={styles.accountCreatedText}>{t('otp.accountCreated')}</Text>
      </View>

      <View style={styles.heartContainer}>
        <View style={[styles.heartCircle, { backgroundColor: hexToRgba(forcedDarkTheme.colors.primary, 0.15) }]}>
          <Heart size={40} color={forcedDarkTheme.colors.primary} fill={forcedDarkTheme.colors.primary} />
        </View>
      </View>

      <Text style={styles.marketingTitle}>{t('otp.stayUpdatedTitle')}</Text>
      <Text style={styles.marketingDescription}>{t('otp.stayUpdatedDescription')}</Text>

      <TouchableOpacity
        style={[styles.primaryButton, isLoading && styles.buttonDisabled]}
        onPress={() => handleMarketingConsent(true)}
        disabled={isLoading}
      >
        {isLoading ? (
          <ActivityIndicator color="#ffffff" />
        ) : (
          <Text style={styles.primaryButtonText}>{t('otp.subscribe')}</Text>
        )}
      </TouchableOpacity>

      <TouchableOpacity
        style={styles.declineButton}
        onPress={() => handleMarketingConsent(false)}
        disabled={isLoading}
      >
        <Text style={styles.declineButtonText}>{t('otp.noThanks')}</Text>
      </TouchableOpacity>
    </View>
  );

  return (
    <View style={styles.container}>
      {currentStep === 'email' && renderEmailStep()}
      {currentStep === 'otp' && renderOTPStep()}
      {currentStep === 'marketing' && renderMarketingStep()}
    </View>
  );
});

export default OTPLoginFlow;
